import { useEffect, useState } from "react";
import { format, isToday, isYesterday } from "date-fns";
import { Plus, MessageSquare, Trash2, ChevronLeft, Search, X, Bug, Bookmark, MoreVertical, Link, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import ExternalInterface from "@/pages/ExternalInterface";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import ConversationDebugDialog from "@/components/ConversationDebugDialog";

interface ConversationOwner {
  full_name: string | null;
  avatar_url: string | null;
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  last_message_sent?: string | null;
  user_id?: string;
  owner?: ConversationOwner | null;
  profile_name?: string | null;
  bookmarked?: boolean;
}

interface ChatSidebarProps {
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onToggle?: () => void;
  streamingConvIds?: string[];
}

type TabType = "all" | "mine" | "bookmarked";

const ChatSidebar = ({ activeId, onSelect, onNew, onToggle, streamingConvIds = [] }: ChatSidebarProps) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [debugConvId, setDebugConvId] = useState<string | null>(null);
  const [exploreOpen, setExploreOpen] = useState(false);
  const { signOut, user, profile } = useAuth();

  const fetchConversations = async () => {
    if (!user) return;

    if (activeTab === "mine") {
      const { data } = await supabase
        .from("conversations")
        .select("id, title, created_at, user_id, last_message_sent, profile_name, bookmarked")
        .eq("user_id", user.id)
        .eq("type", "chat")
        .is("deleted_at", null)
        .order("last_message_sent", { ascending: false });
      if (data) setConversations(data.map((c) => ({ ...c, owner: null, profile_name: (c as any).profile_name ?? null, bookmarked: !!(c as any).bookmarked })));
    } else if (activeTab === "bookmarked") {
      const { data } = await supabase
        .from("conversations")
        .select("id, title, created_at, user_id, last_message_sent, profile_name, bookmarked, profiles:profile_id(full_name, avatar_url)")
        .eq("bookmarked", true)
        .eq("type", "chat")
        .is("deleted_at", null)
        .order("last_message_sent", { ascending: false });
      if (data) {
        setConversations(data.map((c: any) => ({
          id: c.id, title: c.title, created_at: c.created_at, last_message_sent: c.last_message_sent,
          user_id: c.user_id, owner: c.profiles ?? null, profile_name: c.profile_name ?? null, bookmarked: true,
        })));
      } else {
        setConversations([]);
      }
    } else {
      // Fetch client conversations
      let clientQuery = supabase
        .from("conversations")
        .select("id, title, created_at, user_id, last_message_sent, profile_name, bookmarked, profiles:profile_id(full_name, avatar_url)")
        .eq("type", "chat")
        .is("deleted_at", null)
        .order("last_message_sent", { ascending: false });

      if (profile?.client_id) {
        clientQuery = clientQuery.eq("client_id", profile.client_id);
      }

      const [clientRes, ownRes] = await Promise.all([
        clientQuery,
        supabase
          .from("conversations")
          .select("id, title, created_at, user_id, last_message_sent, profile_name, bookmarked, profiles:profile_id(full_name, avatar_url)")
          .eq("user_id", user.id)
          .eq("type", "chat")
          .is("deleted_at", null)
          .order("last_message_sent", { ascending: false }),
      ]);

      const clientData = clientRes.data ?? [];
      const ownData = ownRes.data ?? [];

      // Merge and deduplicate
      const seen = new Set<string>();
      const merged: any[] = [];
      for (const c of [...clientData, ...ownData]) {
        if (!seen.has(c.id)) {
          seen.add(c.id);
          merged.push(c);
        }
      }

      if (merged.length) {
        setConversations(
          merged.map((c: any) => ({
            id: c.id,
            title: c.title,
            created_at: c.created_at,
            last_message_sent: c.last_message_sent,
            user_id: c.user_id,
            owner: c.profiles ?? null,
            profile_name: c.profile_name ?? null,
            bookmarked: !!c.bookmarked,
          })),
        );
      } else {
        setConversations([]);
      }
    }
  };

  useEffect(() => {
    if (user) fetchConversations();
  }, [user, activeId, activeTab]);

  const deleteConversation = async (id: string) => {
    // Soft-delete: mark conversation (and related dashboards) as deleted
    const now = new Date().toISOString();
    await supabase.from("dashboard_snapshots").update({ deleted_at: now, deleted_by: user?.id } as any).eq("conversation_id", id).is("deleted_at", null);
    await supabase.from("conversations").update({ deleted_at: now, deleted_by: user?.id } as any).eq("id", id);
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (activeId === id) onNew();
    setDeleteTarget(null);
  };

  const toggleBookmark = async (conv: Conversation, e: React.MouseEvent) => {
    e.stopPropagation();
    const newVal = !conv.bookmarked;
    setConversations((prev) =>
      prev.map((c) => (c.id === conv.id ? { ...c, bookmarked: newVal } : c))
    );
    await supabase.from("conversations").update({ bookmarked: newVal } as any).eq("id", conv.id);
  };

  const copyConversationUrl = async (convId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = new URL(window.location.origin + "/conversations");
    url.searchParams.set("conv", convId);
    await navigator.clipboard.writeText(url.toString());
    toast.success("URL copied to clipboard");
  };

  const isOwnConversation = (conv: Conversation) => conv.user_id === user?.id;

  return (
    <div className="flex h-full w-64 flex-col border-r border-border bg-sidebar">
      <div className="flex items-center gap-2 p-3">
        <Button onClick={onNew} className="flex-1 justify-start gap-2" variant="outline" size="sm">
          <Plus className="h-4 w-4" />
          New research
        </Button>
        {onToggle && (
          <Button
            onClick={onToggle}
            variant="ghost"
            size="icon"
            className="hidden md:flex h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
            title="Collapse sidebar"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border mx-2">
        <button
          onClick={() => setActiveTab("all")}
          className={cn(
            "flex-1 py-1.5 text-xs font-medium transition-colors border-b-2",
            activeTab === "all"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          All Chats
        </button>
        <button
          onClick={() => setActiveTab("mine")}
          className={cn(
            "flex-1 py-1.5 text-xs font-medium transition-colors border-b-2",
            activeTab === "mine"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          My Chats
        </button>
        <button
          onClick={() => setActiveTab("bookmarked")}
          className={cn(
            "flex-1 py-1.5 text-xs font-medium transition-colors border-b-2",
            activeTab === "bookmarked"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          <Bookmark className={cn("h-3 w-3 inline mr-0.5", activeTab === "bookmarked" && "fill-primary")} />
          Saved
        </button>
      </div>

      {/* Search */}
      <div className="px-2 py-1.5">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search conversations..."
            className="h-7 pl-7 pr-7 text-xs"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1 px-2">
        <div className="space-y-1 py-2">
          {conversations
            .filter((c) => {
              const q = searchQuery.toLowerCase();
              return c.title.toLowerCase().includes(q) || (c.profile_name || "").toLowerCase().includes(q);
            })
            .map((c) => (
            <div
              key={c.id}
              onClick={() => onSelect(c.id)}
              className={cn(
                "group flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors cursor-pointer",
                activeId === c.id
                  ? "bg-sidebar-accent text-sidebar-accent-foreground"
                  : "text-sidebar-foreground hover:bg-sidebar-accent/50",
              )}
            >
              <div className="relative shrink-0 mt-0.5">
                {c.bookmarked ? (
                  <Bookmark className="h-4 w-4 fill-primary text-primary" />
                ) : (
                  <MessageSquare className="h-4 w-4" />
                )}
                {streamingConvIds.includes(c.id) && (
                  <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span className="block break-words leading-snug">{c.title}</span>
                <div className="flex items-center justify-between mt-0.5 gap-1">
                  <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                    {activeTab === "all" && c.owner && (
                      <>
                        <Avatar className="h-3.5 w-3.5 shrink-0">
                          <AvatarImage src={c.owner.avatar_url ?? undefined} />
                          <AvatarFallback className="text-[6px] bg-muted">
                            {(c.owner.full_name || "?").charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-[10px] text-muted-foreground truncate max-w-[60px]">
                          {c.owner.full_name || "Unknown"}
                        </span>
                        <span className="text-[10px] text-muted-foreground shrink-0">·</span>
                      </>
                    )}
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {(() => {
                        const d = new Date(c.last_message_sent || c.created_at);
                        return isToday(d)
                          ? format(d, "h:mm a")
                          : isYesterday(d)
                            ? `Yesterday, ${format(d, "h:mm a")}`
                            : format(d, "MMM d, h:mm a");
                      })()}
                    </span>
                  </div>
                  <div className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-3 w-3 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[150px]" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={(e) => toggleBookmark(c, e as any)}>
                          <Bookmark className={cn("h-3.5 w-3.5 mr-2", c.bookmarked && "fill-primary text-primary")} />
                          {c.bookmarked ? "Remove Bookmark" : "Bookmark"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => copyConversationUrl(c.id, e as any)}>
                          <Link className="h-3.5 w-3.5 mr-2" />
                          Copy URL
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setDebugConvId(c.id); }}>
                          <Bug className="h-3.5 w-3.5 mr-2" />
                          Debug
                        </DropdownMenuItem>
                        {isOwnConversation(c) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={(e) => { e.stopPropagation(); setDeleteTarget(c.id); }}
                            >
                              <Trash2 className="h-3.5 w-3.5 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {conversations.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-4">No conversations yet.</p>
          )}
        </div>
      </ScrollArea>

      {/* WhatsApp / Messaging ticker */}
      <button
        onClick={() => setExploreOpen(true)}
        className="mx-2 mb-2 rounded-lg border border-border bg-muted/50 p-2.5 flex items-start gap-2.5 text-left cursor-pointer hover:bg-muted transition-colors"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0 text-[hsl(142,70%,45%)]" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] leading-tight text-foreground font-medium">
            Chat with your data directly on WhatsApp and other messaging apps
          </p>
          <span className="text-[11px] text-primary">Explore →</span>
        </div>
      </button>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete conversation?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this conversation and all its messages. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteConversation(deleteTarget)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <ConversationDebugDialog
        conversationId={debugConvId}
        open={!!debugConvId}
        onOpenChange={(open) => !open && setDebugConvId(null)}
      />

      <Dialog open={exploreOpen} onOpenChange={setExploreOpen}>
        <DialogContent className="max-w-[90vw] w-[90vw] h-[85vh] p-0 overflow-hidden flex flex-col">
          <div className="flex-1 min-h-0 overflow-auto flex">
            <ExternalInterface />
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ChatSidebar;
